import { useEffect, useState } from 'react';
import { Box, FormControl, MenuItem, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import type { FieldValidation } from '@rjsf/utils';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import { LabelWithTooltip, useScaffolderTooltip } from '../LabelWithTooltip';
import { getCatalogCandidates } from './catalogOptions';
import { DEFAULT_CATALOG_OPTIONS } from './catalogFallbackValues';
import { useUserApplications } from '../../../hooks/useUserApplications';

// -----------------------------------------------------------------------------------
// Mandatory organization data for the IaC MVP golden path.
//
// Recoge:
//   - Aplicación de Negocio: entidad de tipo `Application` del catálogo.
//   - Servicio de aplicación: se puebla a partir de `spec.applicationServices`
//     (cmdb_ci_service) de la aplicación seleccionada, importados desde la CMDB.
//   - Catalog: se DERIVA del servicio elegido (`catalog`, el u_catalog de la CMDB).
//     Si el servicio trae exactamente un valor, el campo se autorrellena y queda en
//     solo lectura. Si trae más de uno, o no trae ninguno, se ofrece un Select: con
//     los valores reales del servicio en el primer caso, o con los 3 valores
//     predefinidos (DEFAULT_CATALOG_OPTIONS) en el segundo. Vive aquí, y no como
//     parámetro suelto del template, para que el valor derivado se calcule donde vive
//     su origen y no puedan desfasarse.
//
// The Environment (DEV/PRE/PRO) is collected as a separate native selector in the
// template, since it doesn't depend on the selected application.
// -----------------------------------------------------------------------------------

interface ApplicationService {
  name: string;
  id: string;
  relationType?: string;
  /**
   * u_catalog del servicio en la CMDB. Ausente en los servicios que no lo tienen
   * informado. Hoy la CMDB solo persiste un único valor por servicio, pero se admite
   * también `string[]` de forma defensiva/preparada para un futuro modelo 1:N.
   */
  catalog?: string | string[];
}

export const OrgDataIaCField = ({
  onChange,
  rawErrors,
  required,
  formData,
}: FieldExtensionComponentProps<any>) => {
  // Only fetches the `Application` entities related to the current user
  // (business_manager, it_owner, project role or division manager — OpenFGA
  // `can_list` relation), unless they are platform_admin or a member of
  // oficina-devops, who see all of them.
  const { applications } = useUserApplications();

  const applicationTooltip = useScaffolderTooltip('iacMvp.application');
  const applicationServiceTooltip = useScaffolderTooltip('iacMvp.applicationService');
  const catalogTooltip = useScaffolderTooltip('iacMvp.catalog');

  const [applicationRef, setApplicationRef] = useState('');
  const [applicationServices, setApplicationServices] = useState<ApplicationService[]>([]);
  const [applicationServiceId, setApplicationServiceId] = useState('');
  // Solo el catálogo ELEGIDO en el Select necesita estado: cuando el servicio trae
  // exactamente un valor de la CMDB se deriva directamente de él (ver
  // `effectiveCatalog` más abajo) y no pasa por este estado.
  const [selectedCatalog, setSelectedCatalog] = useState('');

  // ---------------------------------------------------------------------------------
  // 1. Sync with formData (navigating back/forward between steps)
  // ---------------------------------------------------------------------------------
  useEffect(() => {
    if (!formData) return;
    setApplicationRef(formData.applicationRef || '');
    setApplicationServiceId(formData.applicationServiceId || '');
    // Sin esto, al navegar hacia atrás y volver se perdería la selección hecha en el
    // Select. Cuando el servicio trae exactamente un catalog de la CMDB este valor se
    // ignora, porque `effectiveCatalog` da precedencia al valor fijo.
    setSelectedCatalog(formData.catalog || '');
  }, [formData]);

  // ---------------------------------------------------------------------------------
  // 2. Repopulate application services when an application is already selected
  //    (e.g. when going back to the step) and the applications are already loaded.
  // ---------------------------------------------------------------------------------
  useEffect(() => {
    if (!applicationRef || applications.length === 0) return;
    const selected = applications.find(
      a => `application:${a.metadata.namespace}/${a.metadata.name}` === applicationRef,
    );
    const services = (selected?.spec?.applicationServices ?? []) as unknown as ApplicationService[];
    setApplicationServices(services);
  }, [applicationRef, applications]);

  // ---------------------------------------------------------------------------------
  // 3. Business Application selection
  // ---------------------------------------------------------------------------------
  const handleApplicationChange = (app: any) => {
    const ref = `application:${app.metadata.namespace}/${app.metadata.name}`;
    const services = (app.spec?.applicationServices ?? []) as ApplicationService[];

    setApplicationRef(ref);
    setApplicationServices(services);
    // Changing the application resets the previously chosen service, and with it
    // the catalog, since it depends on that service.
    setApplicationServiceId('');
    setSelectedCatalog('');

    onChange({
      application: app.spec?.number || '',
      applicationId: app.spec?.id || '',
      applicationName: app.metadata.name,
      // The namespace travels separately from the entityRef so the template can
      // build the application page URL (`/catalog/<namespace>/
      // application/<name>/iac`) without having to split the ref.
      applicationNamespace: app.metadata.namespace ?? 'default',
      applicationTitle: app.metadata.title || app.metadata.name,
      applicationRef: ref,
      applicationService: '',
      applicationServiceId: '',
      catalog: '',
    });
  };

  // ---------------------------------------------------------------------------------
  // 4. Application Service selection
  // ---------------------------------------------------------------------------------
  const handleServiceChange = (service: ApplicationService | null) => {
    setApplicationServiceId(service?.id || '');
    // Se descarta la selección hecha para el servicio anterior: arrastrarla haría que
    // un catálogo elegido a mano acabara asociado a un servicio que no es el suyo.
    setSelectedCatalog('');

    const candidates = getCatalogCandidates(service?.catalog);
    // Con exactamente un candidato el catalog se precarga, igual que hoy; con 0 o >1
    // queda vacío hasta que el usuario elija en el Select.
    const catalog = candidates.length === 1 ? candidates[0] : '';

    onChange({
      ...(formData ?? {}),
      applicationService: service?.name || '',
      applicationServiceId: service?.id || '',
      catalog,
    });
  };

  // ---------------------------------------------------------------------------------
  // 5. Catalog elegido en el Select (cuando el servicio trae 0 o >1 valores de CMDB)
  // ---------------------------------------------------------------------------------
  const handleCatalogSelect = (value: string) => {
    setSelectedCatalog(value);

    onChange({
      ...(formData ?? {}),
      catalog: value,
    });
  };

  // ---------------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------------
  // El catálogo de la CMDB manda cuando el servicio trae exactamente un valor: el
  // campo queda fijo y de solo lectura, y `selectedCatalog` (que pueda quedar de un
  // servicio anterior) se ignora. Con 0 o >1 valores se ofrece un Select: con los
  // valores reales del servicio, o con los predefinidos si no trae ninguno.
  const serviceCatalog = applicationServices.find(s => s.id === applicationServiceId)?.catalog;
  const catalogCandidates = getCatalogCandidates(serviceCatalog);
  const catalogIsFixed = catalogCandidates.length === 1;
  const catalogSelectOptions: readonly string[] =
    catalogCandidates.length > 0 ? catalogCandidates : DEFAULT_CATALOG_OPTIONS;
  const effectiveCatalog = catalogIsFixed ? catalogCandidates[0] : selectedCatalog;

  const serviceHelperText = () => {
    if (!applicationRef) return 'Selecciona primero una Aplicación de Negocio';
    if (applicationServices.length === 0) {
      return 'La aplicación seleccionada no tiene servicios de aplicación en la CMDB';
    }
    return 'Selecciona el Servicio de aplicación';
  };

  const catalogHelperText = () => {
    if (!applicationServiceId) return 'Selecciona primero un Servicio de aplicación';
    if (catalogIsFixed) {
      return 'Obtenido de la CMDB a partir del Servicio de aplicación seleccionado';
    }
    if (catalogCandidates.length > 1) {
      return 'El Servicio de aplicación tiene varios catalogs en la CMDB: selecciona uno';
    }
    return 'La CMDB no aporta catálogo para este servicio: selecciona uno de los valores predefinidos';
  };

  return (
    <FormControl margin="normal" required={required} fullWidth>
      {/* Aplicación de Negocio.
          El label va fuera del input (LabelWithTooltip) para poder acompañarlo del
          icono de ayuda, así que el TextField ya no declara `label` ni `required`. */}
      <Box mb={2}>
        <LabelWithTooltip
          label="Aplicación de Negocio"
          tooltip={applicationTooltip}
          required
        />
        <Autocomplete
          options={applications}
          getOptionLabel={opt => opt.metadata.title || opt.metadata.name || ''}
          value={
            applications.find(
              a => `application:${a.metadata.namespace}/${a.metadata.name}` === applicationRef,
            ) || null
          }
          onChange={(_, newValue) => {
            if (newValue) handleApplicationChange(newValue);
          }}
          renderInput={params => (
            <TextField
              {...params}
              helperText={rawErrors?.[0] || 'Selecciona la Aplicación de Negocio'}
            />
          )}
        />
      </Box>

      {/* Servicio de aplicación */}
      <Box>
        <LabelWithTooltip
          label="Servicio de aplicación"
          tooltip={applicationServiceTooltip}
          required
        />
        <Autocomplete
          options={applicationServices}
          disabled={!applicationRef}
          getOptionLabel={opt => opt.name || ''}
          getOptionSelected={(opt, val) => opt.id === val.id}
          value={applicationServices.find(s => s.id === applicationServiceId) || null}
          onChange={(_, newValue) => handleServiceChange(newValue)}
          renderInput={params => (
            <TextField
              {...params}
              helperText={serviceHelperText()}
            />
          )}
        />
      </Box>

      {/* Catalog: derivado del Servicio de aplicación. Fijo y de solo lectura cuando
          hay exactamente un valor; Select (con los valores reales, o los predefinidos
          si no hay ninguno) en el resto de casos.
          A diferencia de los dos campos de arriba, este input sí lleva `aria-label`:
          el label de LabelWithTooltip se pinta fuera y no está asociado al input, así
          que sin él el campo no tendría nombre accesible. */}
      <Box mt={2}>
        <LabelWithTooltip label="Catalog" tooltip={catalogTooltip} required />
        <TextField
          fullWidth
          data-testid="catalog-field"
          select={!catalogIsFixed}
          value={effectiveCatalog}
          disabled={!applicationServiceId || catalogIsFixed}
          onChange={event => handleCatalogSelect(event.target.value)}
          helperText={catalogHelperText()}
          inputProps={{ 'aria-label': 'Catalog' }}
        >
          {!catalogIsFixed &&
            catalogSelectOptions.map(option => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
        </TextField>
      </Box>
    </FormControl>
  );
};

// -----------------------------------------------------------------------------------
// Validation
// -----------------------------------------------------------------------------------
// `catalog` is validated HERE and not with the template's `required`: since it lives
// inside the `orgData` object, RJSF no longer sees it as its own parameter and
// wouldn't enforce it.
export const orgDataIaCValidation = (
  value: { application?: string; applicationService?: string; catalog?: string },
  validation: FieldValidation,
) => {
  if (!value?.application) {
    validation.addError('La Aplicación de Negocio es obligatoria.');
  }
  if (!value?.applicationService) {
    validation.addError('El Servicio de aplicación es obligatorio.');
  }
  if (!value?.catalog) {
    validation.addError('El Catalog es obligatorio.');
  }
};

// -----------------------------------------------------------------------------------
// Custom Field registration
// -----------------------------------------------------------------------------------
export const OrgDataIaCCaseFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'OrgDataIaCField',
    component: OrgDataIaCField,
    validation: orgDataIaCValidation,
  }),
);
